/**
 * Inserting the things that are not prose: callouts, buttons, screen references.
 *
 * These are the parts of the format an author would otherwise have to remember
 * character for character, and the parts that fail loudest when mistyped — a
 * button whose target is not an in-app path is refused outright.
 *
 * The target is picked from the agent's catalog rather than typed, so the link is
 * a route the app actually has. That is the same catalog `{{Screen name}}`
 * resolves against, which is why picking from it makes a reference clickable
 * instead of merely a pill.
 */

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  AGENT_TARGETS,
  type AgentTarget,
} from "@/features/agent-chat/actions/agentTargets"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconAlertTriangle,
  IconBulb,
  IconPointer,
  IconSquareRoundedArrowRight,
} from "@tabler/icons-react"
import { useMemo, useState } from "react"
import { foldForSearch } from "../search"

type Picker = "cta" | "reference" | null

interface Screen {
  label: string
  route: string
}

/** Catalog entries that have a usable label, de-duplicated by label. */
function useScreens(): Screen[] {
  const { translate } = useTranslation()
  return useMemo(() => {
    const byLabel = new Map<string, Screen>()
    for (const target of Object.values(AGENT_TARGETS) as AgentTarget[]) {
      const label = target.labelKey ? translate(target.labelKey) : target.label
      if (!label || byLabel.has(label)) continue
      byLabel.set(label, { label, route: target.route })
    }
    return [...byLabel.values()].sort((a, b) => a.label.localeCompare(b.label))
  }, [translate])
}

function ScreenPicker({
  kind,
  onPick,
  onClose,
}: {
  kind: Exclude<Picker, null>
  onPick: (screen: Screen) => void
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const screens = useScreens()
  const [query, setQuery] = useState("")

  const matches = useMemo(() => {
    const folded = foldForSearch(query.trim())
    if (!folded) return screens
    return screens.filter((screen) =>
      foldForSearch(screen.label).includes(folded),
    )
  }, [screens, query])

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {translate(
              kind === "cta"
                ? "HELPDESK/INSERT_BUTTON"
                : "HELPDESK/INSERT_REFERENCE",
            )}
          </DialogTitle>
          <DialogDescription>
            {translate(
              kind === "cta"
                ? "HELPDESK/INSERT_BUTTON_HINT"
                : "HELPDESK/INSERT_REFERENCE_HINT",
            )}
          </DialogDescription>
        </DialogHeader>

        <Input
          autoFocus
          value={query}
          placeholder={translate("HELPDESK/INSERT_SEARCH")}
          onChange={(event) => setQuery(event.target.value)}
        />

        <ul className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
          {matches.map((screen) => (
            <li key={screen.label}>
              <button
                type="button"
                onClick={() => onPick(screen)}
                className="flex w-full flex-col items-start rounded-md px-2 py-1.5 text-left hover:bg-white/5"
              >
                <span className="text-xs text-white">{screen.label}</span>
                <span className="font-mono text-[11px] text-white/35">
                  {screen.route}
                </span>
              </button>
            </li>
          ))}
          {matches.length === 0 ? (
            <li className="px-2 py-1.5 text-xs text-white/40">
              {translate("HELPDESK/INSERT_NO_MATCH")}
            </li>
          ) : null}
        </ul>
      </DialogContent>
    </Dialog>
  )
}

export function HelpdeskInsertToolbar({
  onInsert,
}: {
  /** Receives a snippet to drop in at the cursor. */
  onInsert: (snippet: string) => void
}) {
  const { translate } = useTranslation()
  const [picker, setPicker] = useState<Picker>(null)

  const items = [
    {
      key: "tip",
      icon: IconBulb,
      label: translate("HELPDESK/INSERT_TIP"),
      onClick: () =>
        onInsert(`:::tip\n${translate("HELPDESK/INSERT_PLACEHOLDER")}\n:::\n`),
    },
    {
      key: "warning",
      icon: IconAlertTriangle,
      label: translate("HELPDESK/INSERT_WARNING"),
      onClick: () =>
        onInsert(
          `:::warning\n${translate("HELPDESK/INSERT_PLACEHOLDER")}\n:::\n`,
        ),
    },
    {
      key: "cta",
      icon: IconSquareRoundedArrowRight,
      label: translate("HELPDESK/INSERT_BUTTON"),
      onClick: () => setPicker("cta"),
    },
    {
      key: "reference",
      icon: IconPointer,
      label: translate("HELPDESK/INSERT_REFERENCE"),
      onClick: () => setPicker("reference"),
    },
  ]

  const pick = (screen: Screen) => {
    onInsert(
      picker === "cta"
        ? `:::cta {href="${screen.route}" label="${screen.label}"}\n${translate("HELPDESK/INSERT_PLACEHOLDER")}\n:::\n`
        : `{{${screen.label}}}`,
    )
    setPicker(null)
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-1">
        {items.map((item) => (
          <Button
            key={item.key}
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 text-grey5 hover:text-white"
            onClick={item.onClick}
          >
            <item.icon size={13} className="mr-1" />
            {item.label}
          </Button>
        ))}
      </div>
      {picker ? (
        <ScreenPicker
          kind={picker}
          onPick={pick}
          onClose={() => setPicker(null)}
        />
      ) : null}
    </>
  )
}
